import React, { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect } from 'react';
import { useFilterManagement } from '../../hooks/useFilterManagement';
import { getMoreFiltersData } from '../../api/entities_api';
import { BASE_COLUMNS, ADDITIONAL_COLUMNS } from '../../constants';
import {
  getFieldLabels,
  getNumberFieldConfig,
  isGroupableField,
  isNestedGroupingField,
  processNestedGrouping
} from '../../utils/fieldConfig';
import { UI_CONSTANTS } from '../../constants';
import FilterPopup from './FilterPopup';
import '../../css/AdvancedSearchDropdown.css';

export default function MainScreenFilters({
  urlParams = null,
  urlFilters = {},
  updateUrlParams,
  entitiesData = null,
  entitiesLoading = false,
  onSearchChange,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [showFilterFor, setShowFilterFor] = useState(null);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [isLoadingMoreFilters, setIsLoadingMoreFilters] = useState(false);
  const dropdownRef = useRef(null);
  const panelRef = useRef(null);
  
  // Filter management hook
  const filterManagement = useFilterManagement(urlFilters);
  
  // Create column configs for MAIN_SCREEN_FILTERS
  const fieldLabels = getFieldLabels();
  const filterColumns = useMemo(() => {
    return BASE_COLUMNS
      .filter(filterKey => filterKey !== 'EntityId' && filterKey !== 'IsRemoved' && filterKey !== 'ProfileName')
      .map(filterKey => ({
        key: filterKey,
        label: fieldLabels[filterKey] || filterKey
      }));
  }, [fieldLabels]);

  // Create column configs for ADDITIONAL_COLUMNS (excluding keys already in filterColumns)
  const additionalFilterColumns = useMemo(() => {
    const baseKeys = new Set(filterColumns.map(col => col.key));
    return ADDITIONAL_COLUMNS
      .filter(filterKey => !baseKeys.has(filterKey))
      .map(filterKey => ({
        key: filterKey,
        label: fieldLabels[filterKey] || filterKey
      }));
  }, [fieldLabels, filterColumns]);
  
  // Get filter groups and max values from entities report data
  const [entitiesReportData, setEntitiesReportData] = useState(entitiesData);

  // Update entitiesReportData when entitiesData prop changes
  useEffect(() => {
    setEntitiesReportData(entitiesData);
  }, [entitiesData]);
  
  // Extract max_values and groups
  const loadedMaxValues = useMemo(() => entitiesReportData?.max_values || null, [entitiesReportData]);
  const loadedFilterGroups = useMemo(() => entitiesReportData?.groups || null, [entitiesReportData]);
  
  // Sync max values and filter groups to filter management
  const prevMaxValuesStrRef = useRef('');
  const prevFilterGroupsStrRef = useRef('');
  const EMPTY_MAX_VALUES = useRef({}).current;
  const EMPTY_FILTER_GROUPS = useRef({}).current;
  
  useEffect(() => {
    const currentMaxValues = loadedMaxValues || EMPTY_MAX_VALUES;
    const currentFilterGroups = loadedFilterGroups || EMPTY_FILTER_GROUPS;
    
    const currentMaxValuesStr = JSON.stringify(currentMaxValues);
    const currentFilterGroupsStr = JSON.stringify(currentFilterGroups);
    const maxValuesChanged = currentMaxValuesStr !== prevMaxValuesStrRef.current;
    const filterGroupsChanged = currentFilterGroupsStr !== prevFilterGroupsStrRef.current;

    if (maxValuesChanged) {
      prevMaxValuesStrRef.current = currentMaxValuesStr;
      filterManagement.setMaxValues(currentMaxValues);
    }
    
    if (filterGroupsChanged) {
      prevFilterGroupsStrRef.current = currentFilterGroupsStr;
      filterManagement.setFilterGroups(currentFilterGroups);
    }
  }, [loadedMaxValues, loadedFilterGroups, filterManagement, EMPTY_MAX_VALUES, EMPTY_FILTER_GROUPS]);
  
  // Sync filterManagement internal state when urlFilters change
  const prevFiltersRef = useRef(null);
  useEffect(() => {
    const currentFiltersStr = JSON.stringify(urlFilters || {});
    const prevFiltersStr = prevFiltersRef.current;
    
    if (currentFiltersStr !== prevFiltersStr) {
      prevFiltersRef.current = currentFiltersStr;
      filterManagement.setFilters(urlFilters || {});
      filterManagement.setPendingFilters(urlFilters || {});
    }
  }, [urlFilters, filterManagement]);

  // Ensure additionalColumns are synced with active filters
  useEffect(() => {
    if (!urlParams || !urlFilters) return;
    
    const activeAdditionalColumns = [];
    Object.keys(urlFilters).forEach(filterKey => {
      const filter = urlFilters[filterKey];
      if (filter && Object.keys(filter).length > 0) {
        if (ADDITIONAL_COLUMNS.includes(filterKey)) {
          activeAdditionalColumns.push(filterKey);
        }
      }
    });
    
    if (activeAdditionalColumns.length === 0) return;
    
    const currentAdditionalColumns = urlParams.additionalColumns || [];
    const mergedColumns = Array.from(new Set([...currentAdditionalColumns, ...activeAdditionalColumns]));
    
    const needsUpdate = mergedColumns.length !== currentAdditionalColumns.length ||
                       !mergedColumns.every((col, idx) => col === currentAdditionalColumns[idx]);
    
    if (needsUpdate) {
      updateUrlParams({
        additionalColumns: mergedColumns
      });
    }
  }, [urlFilters, urlParams, updateUrlParams]);
  
  const getDropdownOptionsForColumn = useCallback((columnKey, serverOptions = []) => {
    if (['Source', 'source'].includes(columnKey) && (!serverOptions || serverOptions.length === 0)) {
      return [
        { value: 'Twitter', label: 'Twitter', color: UI_CONSTANTS.DEFAULT_COLOR },
        { value: 'Facebook', label: 'Facebook', color: UI_CONSTANTS.DEFAULT_COLOR },
        { value: 'Instagram', label: 'Instagram', color: '#833AB4' },
        { value: 'TikTok', label: 'TikTok', color: UI_CONSTANTS.DEFAULT_COLOR },
        { value: 'YouTube', label: 'YouTube', color: UI_CONSTANTS.DEFAULT_COLOR }
      ];
    }
    
    if (isNestedGroupingField(columnKey) && serverOptions.length > 0) {
      return processNestedGrouping(serverOptions, columnKey);
    }
    
    if (isGroupableField(columnKey) &&
        !['Source', 'source'].includes(columnKey)) {
      return serverOptions;
    }
    
    return serverOptions;
  }, []);
  
  const getNumberFieldConfigWithServerData = useCallback((fieldKey) => {
    const baseConfig = getNumberFieldConfig(fieldKey);
    
    if (filterManagement.maxValues[fieldKey] !== undefined && filterManagement.maxValues[fieldKey] !== null) {
      return {
        ...baseConfig,
        max: filterManagement.maxValues[fieldKey]
      };
    }
    
    return baseConfig;
  }, [filterManagement.maxValues]);
  
  const handleRangeChange = useCallback((columnKey, range) => {
    filterManagement.handleRangeChange(columnKey, range, getNumberFieldConfigWithServerData);
  }, [filterManagement, getNumberFieldConfigWithServerData]);
  
  const handleApplyFilters = useCallback(() => {
    const activeAdditionalColumns = [];
    Object.keys(filterManagement.pendingFilters).forEach(filterKey => {
      const filter = filterManagement.pendingFilters[filterKey];
      if (filter && Object.keys(filter).length > 0) {
        if (ADDITIONAL_COLUMNS.includes(filterKey)) {
          activeAdditionalColumns.push(filterKey);
        }
      }
    });
    
    updateUrlParams({
      filters: filterManagement.pendingFilters,
      additionalColumns: activeAdditionalColumns,
      page: 1
    });
    filterManagement.setShowFilterFor(null);
    setShowFilterFor(null);
    setIsOpen(false);
  }, [filterManagement, updateUrlParams]);
  
  const handleClearFilter = useCallback((columnKey) => {
    const newFilters = { ...urlFilters };
    delete newFilters[columnKey];
    
    const currentAdditionalColumns = urlParams?.additionalColumns || [];
    let newAdditionalColumns = [...currentAdditionalColumns];
    
    if (ADDITIONAL_COLUMNS.includes(columnKey)) {
      newAdditionalColumns = newAdditionalColumns.filter(col => col !== columnKey);
      
      const hasOtherActiveAdditionalFilters = Object.keys(newFilters).some(key => {
        if (!ADDITIONAL_COLUMNS.includes(key)) return false;
        const filter = newFilters[key];
        return filter && Object.keys(filter).length > 0;
      });
      
      if (!hasOtherActiveAdditionalFilters) {
        newAdditionalColumns = [];
      }
    }
    
    updateUrlParams({
      filters: newFilters,
      additionalColumns: newAdditionalColumns,
      page: 1
    });
    filterManagement.setShowFilterFor(null);
    setShowFilterFor(null);
    setIsOpen(false);
  }, [urlFilters, urlParams, updateUrlParams, filterManagement]);

  // Clear all filters - updates URL params
  const handleClearAllFilters = useCallback(() => {
    // Clear additional columns since all additional filters are cleared
    updateUrlParams({
      filters: {},
      additionalColumns: [],
      page: 1
    });
    
    // Reset filter management state
    filterManagement.setFilters({});
    filterManagement.setPendingFilters({});
    filterManagement.setShowFilterFor(null);
    setShowFilterFor(null);
    setIsOpen(false);
    onSearchChange?.('');
  }, [updateUrlParams, filterManagement, onSearchChange]);

  useEffect(() => {
    setShowFilterFor(filterManagement.showFilterFor);
  }, [filterManagement.showFilterFor]);
  
  const handleSetShowFilterFor = useCallback((columnKey) => {
    filterManagement.setShowFilterFor(columnKey);
    setShowFilterFor(columnKey);
  }, [filterManagement]);
  
  const handleEscape = useCallback((event) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      setShowFilterFor(null);
      filterManagement.setShowFilterFor(null);
    }
  }, [filterManagement]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking inside the panel
      if (panelRef.current && panelRef.current.contains(event.target)) {
        return;
      }
      // Don't close if clicking the trigger
      if (dropdownRef.current && dropdownRef.current.contains(event.target)) {
        return;
      }
      
      setIsOpen(false);
      setShowFilterFor(null);
      filterManagement.setShowFilterFor(null);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, filterManagement, handleEscape]);

  const handleToggleDropdown = () => {
    setIsOpen(!isOpen);
    if (isOpen) {
      setShowFilterFor(null);
      filterManagement.setShowFilterFor(null);
    }
  };

  const handleToggleMoreFilters = useCallback(() => {
    const newShowMoreFilters = !showMoreFilters;
    setShowMoreFilters(newShowMoreFilters);

    if (newShowMoreFilters) {
      setIsLoadingMoreFilters(true);
      const currentFilters = urlParams?.filters || {};
      getMoreFiltersData({
        filters: currentFilters,
        columns: ['EntityId', ...ADDITIONAL_COLUMNS],
        limit: 25,
        offset: 0,
        sortBy: 'CreatedDate',
        sortOrder: 'desc',
      })
        .then((response) => {
          if (!response) {
            setIsLoadingMoreFilters(false);
            return;
          }
          setEntitiesReportData(prev => ({
            ...(prev || {}),
            max_values: { ...(prev?.max_values || {}), ...(response.max_values || {}) },
            groups: { ...(prev?.groups || {}), ...(response.groups || {}) },
          }));
          setIsLoadingMoreFilters(false);
        })
        .catch((error) => {
          console.error('[MainScreenFilters] getMoreFiltersData failed', error);
          setIsLoadingMoreFilters(false);
        });
    }
  }, [showMoreFilters, urlParams]);

  const activeFiltersCount = useMemo(() => {
    return Object.keys(urlFilters).filter(key => {
      const filter = urlFilters[key];
      if (key === 'flexible_text') {
        return typeof filter === 'string' && filter.trim().length > 0;
      }
      return filter && typeof filter === 'object' && Object.keys(filter).length > 0;
    }).length;
  }, [urlFilters]);

  const hasActiveAdditionalFilter = useMemo(() => {
    return additionalFilterColumns.some(col => 
      urlFilters[col.key] && Object.keys(urlFilters[col.key]).length > 0
    );
  }, [urlFilters, additionalFilterColumns]);
  
  const isLoadingAdditionalColumns = useMemo(() => {
    const hasAdditionalColumns = urlParams?.additionalColumns && 
                                 Array.isArray(urlParams.additionalColumns) && 
                                 urlParams.additionalColumns.length > 0;
    return entitiesLoading && hasAdditionalColumns;
  }, [entitiesLoading, urlParams?.additionalColumns]);

  // Layout Effect to position the dropdown panel
  useLayoutEffect(() => {
    if (isOpen && panelRef.current) {
      const searchBox = document.getElementById('header-search-input-group');
      const analysisDropdown = document.getElementById('header-analysis-dropdown');
      
      if (searchBox && analysisDropdown) {
        const searchRect = searchBox.getBoundingClientRect();
        const analysisRect = analysisDropdown.getBoundingClientRect();
        
        const width = analysisRect.right - searchRect.left;
        
        panelRef.current.style.position = 'fixed';
        panelRef.current.style.top = `${searchRect.bottom + 8}px`;
        panelRef.current.style.left = `${searchRect.left}px`;
        panelRef.current.style.width = `${width}px`;
        panelRef.current.style.maxWidth = `${width}px`;
        panelRef.current.style.minWidth = 'unset';
      }
    }
  }, [isOpen]);
  
  // Combined visible columns based on showMoreFilters
  const allVisibleColumns = useMemo(() => {
    if (showMoreFilters) {
      return [...filterColumns, ...additionalFilterColumns];
    }
    return filterColumns;
  }, [showMoreFilters, filterColumns, additionalFilterColumns]);

  // Helper to render a filter group
  const renderFilterGroup = (column) => {
    const hasActiveFilter = urlFilters[column.key] && Object.keys(urlFilters[column.key]).length > 0;
    return (
      <div key={column.key} className={`filter-group ${showFilterFor === column.key ? 'active-popup' : ''}`}>
        <div 
          className="filter-header" 
          onClick={() => handleSetShowFilterFor(showFilterFor === column.key ? null : column.key)}
        >
          <label>{column.label}</label>
          <span className={`filter-arrow ${showFilterFor === column.key ? 'open' : ''}`}>▼</span>
        </div>
        {hasActiveFilter && (
          <span className="filter-active-badge"></span>
        )}
        <FilterPopup
          column={column}
          showFilterFor={showFilterFor}
          setShowFilterFor={handleSetShowFilterFor}
          pendingFilters={filterManagement.pendingFilters}
          setPendingFilters={filterManagement.setPendingFilters}
          filters={filterManagement.filters}
          filterGroups={filterManagement.filterGroups}
          getDropdownOptionsForColumn={getDropdownOptionsForColumn}
          getNumberFieldConfigWithServerData={getNumberFieldConfigWithServerData}
          handleMultiSelectChange={filterManagement.handleMultiSelectChange}
          handleFilterChange={filterManagement.handleFilterChange}
          handleRangeChange={handleRangeChange}
          applyFilters={handleApplyFilters}
          clearFilter={handleClearFilter}
          isLoadingOptions={isLoadingMoreFilters}
        />
      </div>
    );
  };
  
  return (
    <div className="main-screen-filters" ref={dropdownRef}>
      <div className="filters-trigger-row">
        <button
          type="button"
          className={`dropdown-trigger ${isOpen ? 'active' : ''}`}
          onClick={handleToggleDropdown}
          title="Filters"
        >
          <span className="btn-icon">⏷</span>
          <span className="btn-label">Filters</span>
          <span className={`dropdown-arrow ${isOpen ? 'open' : ''}`}>▼</span>
        </button>
        {activeFiltersCount > 0 && (
          <button
            type="button"
            className="clear-filters-trigger-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleClearAllFilters();
            }}
            title="Clear all filters"
          >
            ×
          </button>
        )}
      </div>

      {/* Dropdown Panel - Filters */}
      {isOpen && (
        <div className={`dropdown-panel${!entitiesData?.groups ? ' filter-bar--loading' : ''}`} ref={panelRef}>
          {/* Loading Overlay */}
          {isLoadingMoreFilters && (
            <div className="dropdown-loading-overlay">
              <div className="dropdown-loading-spinner">
                <div className="spinner"></div>
              </div>
            </div>
          )}
          <div className="dropdown-content">
            {/* Filter Dropdowns - Using CSS Grid */}
            <div className="filters-grid">
              {allVisibleColumns.map(renderFilterGroup)}
            </div>
            
            {/* More Filters Toggle Arrow - centered below main filters */}
            <div className="more-filters-arrow-container">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!isLoadingMoreFilters && !isLoadingAdditionalColumns) {
                    handleToggleMoreFilters();
                  }
                }}
                disabled={isLoadingMoreFilters || isLoadingAdditionalColumns}
                className={`more-filters-arrow-btn ${showMoreFilters ? 'active' : ''}`}
                title={showMoreFilters ? 'Hide additional filters' : 'Show additional filters'}
              >
                <span className={`arrow-icon ${showMoreFilters ? 'open' : ''}`}>▼</span>
              </button>
              {hasActiveAdditionalFilter && (
                <span className="filter-active-badge"></span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
